import { LinkButton } from '@euler/app/components/LinkButton';
import { StackLayout } from '@euler/app/components/StackLayout';
import { AddProductButton } from '@euler/app/flows/products/components/AddProductButton';
import { ProductItemCell } from '@euler/app/flows/products/components/ProductItemCell';
import { useTaskProductManagement } from '@euler/app/flows/task/functions/products/useTaskProductManagement';
import { ConstructionJobGradientPanel } from '@euler/app/flows/task/pages/construction/components/ConstructionJobGradientPanel';
import { EmptyView } from '@euler/components/EmptyView';
import { Icon } from '@euler/components/Icomoon';
import { deriveManifestItemAdopter } from '@euler/functions/product/deriveManifestItemAdopter';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { TaskProductManifestItemOrigin } from '@euler/model/enum';
import {
  ConstructionJobContextDataStaged,
  TaskProductManifestItem,
} from '@euler/model/task-detail';
import { idEquals } from '@euler/utils';
import { memo, useCallback } from 'react';
import { StyleProp, TextStyle, View, ViewStyle } from 'react-native';
import { useTheme } from 'styled-components';

export const ConstructionJobProductList = memo(
  ({
    jobId,
    subject,
    items,
    context,
    style,
    editable = true,
    titleStyle,
    contentContainerStyle,
    onChange,
    onUpdateProductAdoptionConfirmation,
    onEditCloser,
  }: {
    jobId: number;
    subject: string;
    items: TaskProductManifestItem[];
    context: ConstructionJobContextDataStaged | undefined;
    style?: StyleProp<ViewStyle>;
    titleStyle?: StyleProp<TextStyle>;
    contentContainerStyle?: StyleProp<ViewStyle>;
    editable?: boolean;
    onChange?: () => void;
    onUpdateProductAdoptionConfirmation?: () => void;
    onEditCloser?: (item: TaskProductManifestItem) => void;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();

    const {
      hasProducts,
      canAssociateProduct,
      onAddProduct,
      onRemoveProduct,
      onViewProductDetail,
    } = useTaskProductManagement({
      origin: {
        type: TaskProductManifestItemOrigin.Construction,
        constructionJobId: jobId,
      },
    });

    const onAddProductPress = useCallback(() => {
      onAddProduct(subject, {
        callback: ({ hasChanges }) => {
          if (hasChanges) {
            onChange?.();
          }
        },
      });
    }, [onAddProduct, onChange, subject]);

    const onRemoveProductPress = useCallback(
      (item: TaskProductManifestItem) => {
        onRemoveProduct(subject, item.product.id);
      },
      [onRemoveProduct, subject],
    );

    const onViewProductPress = useCallback(
      (item: TaskProductManifestItem) => {
        onViewProductDetail(item);
      },
      [onViewProductDetail],
    );

    const isItemAdopted = (item: TaskProductManifestItem) => {
      return (
        item.jobRefs?.includes(jobId) ||
        context?.productConfirmation?.items.find(
          x =>
            idEquals(x.contractId, item.product.sourceContractId) &&
            idEquals(x.productId, item.product.id),
        )?.adopted
      );
    };

    if (!hasProducts) return null;

    return (
      <ConstructionJobGradientPanel
        title="产品信息"
        style={style}
        titleStyle={titleStyle}
        headerStyle={{ marginBottom: 0 }}
        remark={
          canAssociateProduct &&
          editable && (
            <StackLayout
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              flex={1}
              ml={rpx(10)}
            >
              <LinkButton
                text=""
                disabled={!items.length}
                onPress={onUpdateProductAdoptionConfirmation}
                icon={
                  <Icon
                    name="pencil"
                    size={rpx(12)}
                    color={theme.link}
                    css={`
                      top: ${rpx(2)}px;
                      opacity: ${items.length ? 1 : 0};
                    `}
                  />
                }
              />
              <AddProductButton title="添加产品" onPress={onAddProductPress} />
            </StackLayout>
          )
        }
      >
        <View style={contentContainerStyle}>
          {items.length === 0 ? (
            <EmptyView
              message="暂无相关推荐产品"
              iconSize={rpx(44)}
              showIcon={false}
              noFullHeight={true}
              messageStyle={{
                fontSize: rpx(13),
              }}
              css={`
                padding-bottom: ${rpx(8)}px;
              `}
            />
          ) : (
            items.map(item => (
              <ProductItemCell
                key={item.id}
                item={item}
                name={item.product.name}
                qty={item.qty}
                title={item.product.title}
                coverUrl={item.product.coverUrl}
                showBadge={true}
                removable={editable}
                adopted={isItemAdopted(item)}
                author={item.author}
                adopter={deriveManifestItemAdopter(item, jobId)}
                closer={item.closer ?? undefined}
                onEditCloser={editable ? onEditCloser : undefined}
                onRemove={onRemoveProductPress}
                onPress={onViewProductPress}
                css={`
                  margin: ${rpx(2)}px 0;
                `}
              />
            ))
          )}
        </View>
      </ConstructionJobGradientPanel>
    );
  },
);
